// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { MessageCircle } from "lucide-react";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface ContactTommyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ContactTommyModal: React.FC<ContactTommyModalProps> = ({ 
  open, 
  onOpenChange 
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    if (!user) {
      toast({
        title: "Authentication required", 
        description: "You must be logged in to contact Tommy",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from('tommy_contact_requests')
        .insert({
          user_id: user.id,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          message: message.trim()
        })
        .select()
        .single();

      if (error) throw error;

      // Get user profile information
      const { data: profile } = await supabase
        .from('profiles')
        .select('name, email')
        .eq('user_id', user.id)
        .single();

      // Notify admins via edge function
      try {
        const { data: functionData, error: functionError } = await invokeEdgeFunction('notify-admins-tommy-contact', {
          body: {
            name: name.trim(),
            email: email.trim(), 
            phone: phone.trim(),
            message: message.trim(),
            userEmail: profile?.email || user.email,
            userName: profile?.name || 'Unknown User',
            requestId: data.id
          }
        });

        if (functionError) {
          console.error('Failed to notify admins:', functionError);
        } else {
          console.log('Admins notified successfully:', functionData);
        }
      } catch (notifyError) {
        console.error('Error notifying admins:', notifyError);
        // Don't fail the submission if admin notification fails
      }

      toast({
        title: "Message sent!",
        description: "Your message has been sent to Tommy. You'll hear back within 24 hours.",
      });
      
      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting contact request:', error);
      toast({
        title: "Something went wrong",
        description: "We couldn't send your message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
            <MessageCircle className="w-6 h-6" />
            Contact Tommy Canale
          </DialogTitle>
          <DialogDescription>
            Connect with our Lead Scout for personalized recruiting guidance and expert scouting services.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell Tommy about your recruiting needs, questions about the scouting process, or anything else you'd like to discuss..."
                className="min-h-[120px] resize-none"
                required
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button 
              variant="outline" 
              type="button" 
              onClick={() => onOpenChange(false)}
              className="sm:ml-2"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || !name.trim() || !email.trim() || !message.trim()}
              className="bg-secondary hover:bg-secondary/90 text-black font-semibold"
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactTommyModal;